import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileBottomBar from '../../components/feature/MobileBottomBar';
import { useEffect, useState } from 'react';
import { useSEO } from '../../utils/seo';

const ReviewsPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'Customer Reviews | 4.9/5 Rating | Dan & Van Removals Lancashire',
    description: 'Read genuine customer reviews of Dan & Van Removals. 4.9/5 rating from satisfied customers across Lancashire. Professional, reliable moving service.',
    keywords: 'removals reviews Lancashire, customer testimonials, moving company reviews, Dan Van reviews, trusted movers',
    canonical: '/reviews',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Customer Reviews',
      description: 'Genuine customer reviews and testimonials for Dan & Van Removals Lancashire.',
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeFilter, setActiveFilter] = useState('all');

  const reviews = [
    {
      name: 'Sarah Mitchell',
      location: 'Accrington',
      rating: 5,
      service: 'house',
      text: 'Exceptional service from start to finish. The team was professional, careful with our belongings, and made our house move completely stress-free. They arrived on time, worked efficiently, and nothing was damaged. Highly recommend Dan & Van for anyone moving in Lancashire.',
      date: 'December 2024',
    },
    {
      name: 'James Thompson',
      location: 'Blackburn',
      rating: 5,
      service: 'office',
      text: 'Used Dan & Van for our office relocation. They worked efficiently over the weekend so we had zero downtime. The team was incredibly organised, labelled everything clearly, and had us up and running in our new office by Monday morning. Outstanding service.',
      date: 'November 2024',
    },
    {
      name: 'Emma Louise',
      location: 'Clayton-le-Moors',
      rating: 5,
      service: 'single',
      text: 'Needed a piano moved urgently. They came the next day, handled it with such care, and the price was very reasonable. The team knew exactly what they were doing and made what could have been a stressful experience completely hassle-free. Brilliant service.',
      date: 'December 2024',
    },
    {
      name: 'Michael Roberts',
      location: 'Burnley',
      rating: 5,
      service: 'house',
      text: 'We\'ve moved house three times and this was by far the best experience. Dan & Van were professional, friendly, and nothing was too much trouble. They even helped us rearrange furniture in the new house. Worth every penny.',
      date: 'October 2024',
    },
    {
      name: 'Rachel Green',
      location: 'Preston',
      rating: 5,
      service: 'delivery',
      text: 'Fantastic collection and delivery service. I bought a sofa from Facebook Marketplace and Dan & Van collected it and delivered it to my home the same day. Great communication throughout and very careful with the item. Will definitely use again.',
      date: 'November 2024',
    },
    {
      name: 'David Wilson',
      location: 'Lancaster',
      rating: 5,
      service: 'office',
      text: 'Professional office move handled perfectly. The team understood the importance of minimal disruption to our business. They worked quickly and carefully, and everything arrived at our new premises in perfect condition. Excellent service.',
      date: 'September 2024',
    },
    {
      name: 'Lisa Anderson',
      location: 'Chorley',
      rating: 5,
      service: 'house',
      text: 'Moving house is stressful but Dan & Van made it so much easier. They were punctual, polite, and incredibly efficient. Our belongings were treated with care and everything arrived safely. The quote was accurate with no hidden costs. Highly recommend.',
      date: 'December 2024',
    },
    {
      name: 'Peter Hughes',
      location: 'Darwen',
      rating: 5,
      service: 'single',
      text: 'Needed a washing machine moved from a shop to my home. Quick, efficient service at a great price. The team was friendly and professional. They even installed it for me. Couldn\'t ask for better service.',
      date: 'October 2024',
    },
    {
      name: 'Sophie Turner',
      location: 'Clitheroe',
      rating: 5,
      service: 'house',
      text: 'Absolutely brilliant service. We had a lot of fragile items and antiques, and everything was wrapped carefully and arrived in perfect condition. The team went above and beyond. We\'ll definitely use Dan & Van for any future moves.',
      date: 'November 2024',
    },
  ];

  const filteredReviews = activeFilter === 'all' 
    ? reviews 
    : reviews.filter(review => review.service === activeFilter);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <MobileBottomBar />

      {/* Hero Section */}
      <section className="relative bg-charcoal text-white py-24 mt-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex justify-center text-gold text-3xl mb-6">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill"></i>
              ))}
            </div>
            <h1 className="font-sans text-6xl font-bold mb-6">
              Customer Reviews
            </h1>
            <p className="text-xl text-white/90 mb-4">
              See what our customers say about our removals service
            </p>
            <div className="flex items-center justify-center gap-4 text-lg">
              <span className="font-sans text-4xl font-bold">5.0/5</span>
              <span className="text-gray-300">Based on genuine customer feedback</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="py-8 bg-gray-50 sticky top-20 z-30 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeFilter === 'all'
                  ? 'bg-gold text-charcoal'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              All Reviews
            </button>
            <button
              onClick={() => setActiveFilter('house')}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeFilter === 'house'
                  ? 'bg-gold text-charcoal'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              House Removals
            </button>
            <button
              onClick={() => setActiveFilter('office')}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeFilter === 'office'
                  ? 'bg-gold text-charcoal'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Office Removals
            </button>
            <button
              onClick={() => setActiveFilter('single')}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeFilter === 'single'
                  ? 'bg-gold text-charcoal'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Single Items
            </button>
            <button
              onClick={() => setActiveFilter('delivery')}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeFilter === 'delivery'
                  ? 'bg-gold text-charcoal'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Collections
            </button>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredReviews.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white border border-gray-200 rounded-lg p-8 shadow-luxury hover:shadow-luxury-hover transition-all"
              >
                <div className="flex text-gold text-lg mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <p className="font-sans text-lg text-charcoal leading-relaxed mb-6">
                  "{review.text}"
                </p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-sans font-bold text-charcoal">{review.name}</p>
                  <p className="text-sm text-gray-600">{review.location}</p>
                  <p className="text-xs text-gray-500 mt-1">{review.date}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-sans text-4xl font-bold text-charcoal mb-6">
            Experience our premium service yourself
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            Join our satisfied customers across Lancashire. Get your free quote today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:07530166035"
              className="px-8 py-4 border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
            >
              Call 07530 166035
            </a>
            <Link
              to="/contact"
              className="px-8 py-4 bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReviewsPage;
